import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAdminUnits,
  fetchAdminRooms,
  fetchAdminOccupancy,
  fetchAdminOccupancyRooms,
  fetchAdminTenanciesAll,
  normalizeUnit,
  normalizeRoom,
} from "../../api/adminData";
import OccupancyMap from "../../components/OccupancyMap";
import {
  UnitTypeBadge,
  UnitAddressActions,
  buildMapsQueryFromUnit,
} from "../../components/OccupancyUnitMeta";
import {
  roomPlanningLabel,
  roomPlanningBadges,
} from "../../utils/unitOccupancyStatus";
import { todayBusinessDate } from "../../utils/datetime";
import { occupancyPercentText } from "../../utils/occupancyPercent";

/**
 * Why a section has no rows. LOADING, EMPTY AND FAILED ARE THREE SENTENCES:
 * "nothing is let today" and "we could not find out" are opposite statements,
 * and before OC2c this page could not tell them apart.
 */
function emptyOccupancyState(status) {
  if (status === "loading") {
    return { text: "Belegungsdaten werden geladen …", attr: "data-occupancy-loading" };
  }
  if (status === "error") {
    return { text: "Belegungsdaten nicht verfügbar", attr: "data-occupancy-unavailable" };
  }
  return { text: "Keine Belegungsdaten gefunden.", attr: "data-occupancy-empty" };
}

/** A metric the page cannot prove right now, drawn the way the repo draws one. */
function metricOrDash(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : "—";
}

function occupancyBarVisual(rate) {
  const r = Number(rate) || 0;
  if (r >= 100) return { fill: "bg-[#3ddc84]", text: "text-[#3ddc84]" };
  if (r >= 50) return { fill: "bg-[#f5a623]", text: "text-[#f5a623]" };
  if (r > 0) return { fill: "bg-[#ff5f6d]", text: "text-[#ff5f6d]" };
  return { fill: "bg-[#4a5070]", text: "text-[#4a5070]" };
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-[#1c2035] bg-[#10121a]">
      <div className="border-b border-[#1c2035] px-[18px] py-[13px]">
        <h3 className="text-[13px] font-medium text-[#edf0f7]">{title}</h3>
        {subtitle ? <p className="mt-[2px] text-[10px] text-[#4a5070]">{subtitle}</p> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Badge({ children, type = "neutral", className = "" }) {
  const styles = {
    success:
      "border border-[rgba(61,220,132,0.2)] bg-[rgba(61,220,132,0.1)] text-[#3ddc84]",
    warning:
      "border border-[rgba(245,166,35,0.2)] bg-[rgba(245,166,35,0.1)] text-[#f5a623]",
    danger:
      "border border-[rgba(255,95,109,0.2)] bg-[rgba(255,95,109,0.1)] text-[#ff5f6d]",
    info: "border border-[rgba(91,156,246,0.2)] bg-[rgba(91,156,246,0.1)] text-[#5b9cf6]",
    neutral: "border border-[#1c2035] bg-[#141720] text-[#8892b0]",
    // Additive (room-planning visual states sprint) — do not alter the types above.
    alert:
      "border border-[rgba(255,122,69,0.3)] bg-[rgba(255,122,69,0.12)] text-[#ff7a45]",
    accent:
      "border border-[rgba(155,124,244,0.25)] bg-[rgba(155,124,244,0.12)] text-[#9d7cf4]",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-[2px] text-[9px] font-semibold ${styles[type]} ${className}`}
    >
      {children}
    </span>
  );
}

function getDisplayStatus(occupiedCount, reservedCount, totalRooms) {
  if (totalRooms === 0) return "Keine Rooms";
  if (occupiedCount === totalRooms) return "Voll belegt";
  if (occupiedCount === 0 && reservedCount === 0) return "Komplett frei";
  if (occupiedCount === 0 && reservedCount > 0) return "Reservierungen vorhanden";
  return "Teilbelegt";
}

function getStatusBadgeType(status) {
  if (status === "Voll belegt") return "success";
  if (status === "Teilbelegt") return "warning";
  if (status === "Komplett frei") return "danger";
  if (status === "Reservierungen vorhanden") return "warning";
  return "neutral";
}

function getUnitDisplayLabel(unit) {
  if (unit.address && unit.city) {
    return `${unit.address}, ${unit.city}`;
  }
  if (unit.address) return unit.address;
  if (unit.name) return unit.name;
  return unit.unitId || unit.id;
}

function AdminOccupancyPage() {
  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [occupancyFromApi, setOccupancyFromApi] = useState(null);
  /**
   * "loading" | "success" | "error" for the ORG-WIDE `GET /admin/occupancy`.
   *
   * `occupancyFromApi` alone cannot carry this: it is `null` before the answer
   * and `null` after a failure, and those two demand opposite sentences. A
   * SUCCESSFUL answer is authoritative whatever it contains -- `units: []` is a
   * measurement ("nothing is in the occupancy population today"), not a gap to
   * paper over.
   */
  const [occupancyStatus, setOccupancyStatus] = useState("loading");
  const [occupancyRoomsByUnit, setOccupancyRoomsByUnit] = useState({});
  /** Unit ids whose `/occupancy/rooms` request has SETTLED — see the effect below. */
  const [occupancyRoomsSettled, setOccupancyRoomsSettled] = useState({});
  // Sent to the API as `on_date`. An explicit parameter WINS over the
  // backend's own Swiss default, so a UTC day here silently overrides a
  // correct server-side answer — the one place in this slice where the
  // wrong day changed what the server computed, not just what we showed.
  const [onDate] = useState(() => todayBusinessDate());

  useEffect(() => {
    fetchAdminUnits()
      .then((data) => setUnits(Array.isArray(data) ? data.map(normalizeUnit) : []))
      .catch(() => setUnits([]));
    fetchAdminRooms()
      .then((data) => setRooms(Array.isArray(data) ? data.map(normalizeRoom) : []))
      .catch(() => setRooms([]));
    fetchAdminTenanciesAll()
      .then((data) => setTenancies(Array.isArray(data) ? data : []))
      .catch(() => setTenancies([]));
    fetchAdminOccupancy()
      .then((data) => {
        setOccupancyFromApi(data);
        setOccupancyStatus("success");
      })
      .catch(() => {
        setOccupancyFromApi(null);
        setOccupancyStatus("error");
      });
  }, []);

  useEffect(() => {
    const coLiving = units.filter((u) => u.type === "Co-Living");
    if (coLiving.length === 0) return;
    let cancelled = false;
    coLiving.forEach((u) => {
      const unitId = u.id ?? u.unitId;
      // A unit with no usable id is never requested, so it is settled at once —
      // otherwise its map would wait for a response that is not coming.
      if (!unitId) return;
      fetchAdminOccupancyRooms({ unit_id: unitId, on_date: onDate })
        .then((data) => {
          if (!cancelled) setOccupancyRoomsByUnit((prev) => ({ ...prev, [unitId]: data }));
        })
        .catch(() => {})
        // SETTLED, NOT SUCCEEDED (OC1 §8). Since OC1 the map no longer invents a
        // room status from the legacy `Room.status` column when the canonical
        // answer is missing — it says "Belegungsdaten nicht verfügbar". That is
        // the right sentence for a failed request and the wrong one for a
        // request still in flight, and `occupancyRoomsByUnit` cannot tell the
        // two apart on its own. This marks the id either way, so a failure
        // reaches the honest state and a pending one keeps the spinner
        // `OccupancyMap` has always had.
        .finally(() => {
          if (!cancelled) {
            setOccupancyRoomsSettled((prev) =>
              prev[unitId] ? prev : { ...prev, [unitId]: true }
            );
          }
        });
    });
    return () => { cancelled = true; };
  }, [units, onDate]);

  const coLivingUnits = useMemo(() => {
    return units.filter((unit) => unit.type === "Co-Living");
  }, [units]);

  // "Freie Zimmer / Verfügbar ab": flatten the per-unit /occupancy/rooms data
  // (loaded for ALL Co-Living units above) into one planning list. Each row's
  // planning_state comes from the backend (source of truth); roomPlanningLabel
  // only maps it to copy. Ordered free → free_then_reserved →
  // occupied_then_reserved → occupied, then by free_from_date ascending.
  const availabilityRooms = useMemo(() => {
    const unitMap = new Map(units.map((u) => [u.id ?? u.unitId, u]));
    const order = {
      free: 0,
      free_then_reserved: 1,
      occupied_then_reserved: 2,
      occupied: 3,
    };
    const out = [];
    for (const [unitId, data] of Object.entries(occupancyRoomsByUnit)) {
      const roomsArr = data && Array.isArray(data.rooms) ? data.rooms : [];
      const unit = unitMap.get(unitId) ?? null;
      for (const room of roomsArr) {
        const label = roomPlanningLabel(room);
        if (!label) continue;
        out.push({ key: `${unitId}:${room.room_id}`, unitId, unit, room, label });
      }
    }
    out.sort((a, b) => {
      const oa = order[a.room.planning_state] ?? 9;
      const ob = order[b.room.planning_state] ?? 9;
      if (oa !== ob) return oa - ob;
      const fa = a.room.free_from_date || "9999-12-31";
      const fb = b.room.free_from_date || "9999-12-31";
      return fa < fb ? -1 : fa > fb ? 1 : 0;
    });
    return out;
  }, [occupancyRoomsByUnit, units]);

  const { apartmentsCount, coLivingCount } = useMemo(() => {
    let apt = 0;
    let cl = 0;
    for (const u of units) {
      if (String(u?.type || "").trim() === "Co-Living") cl += 1;
      else apt += 1;
    }
    return { apartmentsCount: apt, coLivingCount: cl };
  }, [units]);

  /**
   * THE ORG-WIDE OCCUPANCY ROWS, AND THEY COME FROM ONE PLACE ONLY.
   *
   * Until OC2c this memo had a second body: when the canonical answer was
   * missing OR merely EMPTY, the page re-derived the whole thing in the browser
   * from `rooms` + `tenancies` via `getRoomOccupancyStatus`. That was a third
   * occupancy truth, and a demonstrably wrong one -- it had no `Room.is_active`
   * filter, no lease window, and it silently dropped every Apartment, because
   * it iterated `coLivingUnits`. The old characterization pinned all four
   * losses. Layer 2 is owned by `get_org_occupancy` behind
   * `GET /api/admin/occupancy`, and rebuilding it here would mean owning the
   * active-room filter, the lease gate, the unit-type population and the
   * current-day classification a second time.
   *
   * SO THE GATE IS THE REQUEST'S OUTCOME, NOT THE ANSWER'S LENGTH. The old
   * condition `occupancyFromApi.units.length > 0` treated a successful empty
   * answer as no answer. A lease-gated empty result is data: it says that no
   * unit is in today's occupancy population. Replacing it with ungated local
   * counts stated the opposite of what the server had computed.
   */
  const occupancyRows = useMemo(() => {
    if (occupancyStatus !== "success") return [];
    const canonical = Array.isArray(occupancyFromApi?.units)
      ? occupancyFromApi.units
      : [];
    const unitMap = new Map(units.map((u) => [u.id || u.unitId, u]));
    return canonical.map((occ) => {
      const unit = unitMap.get(occ.unit_id);
      const occupiedCount = occ.occupied_rooms ?? 0;
      const reservedCount = occ.reserved_rooms ?? 0;
      const freeCount = occ.free_rooms ?? 0;
      const totalRooms = occ.total_rooms ?? 0;
      const occupancyRate = occ.occupancy_rate ?? (totalRooms ? (occupiedCount / totalRooms) * 100 : 0);
      const reservedRate = totalRooms ? (reservedCount / totalRooms) * 100 : 0;
      const displayStatus = getDisplayStatus(occupiedCount, reservedCount, totalRooms);
      return {
        unitId: occ.unit_id,
        unit: unit ?? null,
        place: unit ? unit.place || unit.city : occ.unit_id,
        address: unit ? unit.address : "",
        totalRooms,
        occupiedCount,
        reservedCount,
        freeCount,
        occupancyRate,
        reservedRate,
        displayStatus,
      };
    });
  }, [occupancyStatus, occupancyFromApi, units]);

  /**
   * THE HEADLINE NUMBERS, OR NOTHING.
   *
   * `null` while loading and after a failure, so the cards can say so instead
   * of printing a zero nobody measured. On success the server's own
   * `summary` is passed through unchanged -- it already defines the zero case
   * (`occupancy_rate` is `0.0` at zero rooms, computed server-side), and this
   * slice does not reinterpret it. The fold over the canonical rows is only a
   * fallback for a response that omits the block; it is arithmetic over the
   * server's own per-unit answers, not a second occupancy rule, and it never
   * touches `rooms` or `tenancies`.
   */
  const summary = useMemo(() => {
    if (occupancyStatus !== "success") {
      return {
        totalRooms: null,
        occupiedRooms: null,
        reservedRooms: null,
        freeRooms: null,
        occupancyRate: null,
      };
    }

    const fromServer = occupancyFromApi?.summary;
    if (fromServer && typeof fromServer.total_rooms === "number") {
      return {
        totalRooms: fromServer.total_rooms,
        occupiedRooms: fromServer.occupied_rooms ?? 0,
        reservedRooms: fromServer.reserved_rooms ?? 0,
        freeRooms: fromServer.free_rooms ?? 0,
        occupancyRate: fromServer.occupancy_rate ?? 0,
      };
    }

    const totalRooms = occupancyRows.reduce((sum, row) => sum + row.totalRooms, 0);
    const occupiedRooms = occupancyRows.reduce((sum, row) => sum + row.occupiedCount, 0);
    const reservedRooms = occupancyRows.reduce((sum, row) => sum + row.reservedCount, 0);
    const freeRooms = occupancyRows.reduce((sum, row) => sum + row.freeCount, 0);
    const occupancyRate = totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;

    return { totalRooms, occupiedRooms, reservedRooms, freeRooms, occupancyRate };
  }, [occupancyStatus, occupancyFromApi, occupancyRows]);

  const weakestUnits = useMemo(() => {
    return [...occupancyRows]
      .sort((a, b) => a.occupancyRate - b.occupancyRate)
      .slice(0, 5);
  }, [occupancyRows]);

  const quotePct =
    summary.occupancyRate == null || !(summary.totalRooms > 0)
      ? 0
      : Math.min(100, Math.max(0, Number(summary.occupancyRate) || 0));

  return (
    <div data-admin-page="belegung" className="-m-6 min-h-screen bg-[#080a0f]">
      <div className="sticky top-0 z-30 flex h-[50px] items-center border-b border-[#1c2035] bg-[#0c0e15] px-6 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <span className="text-[14px] font-medium text-[#edf0f7]">Belegung</span>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 py-5">
        <div>
          <div className="mb-[10px] flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.8px] text-[#4a5070]">
              Übersicht · Live
            </span>
            <div className="h-px flex-1 bg-[#1c2035]" />
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <div className="relative overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#10121a] p-[13px_15px] transition-colors hover:border-[#242840]">
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-t-[10px] bg-[#5b9cf6]" />
              <p className="mb-[4px] text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Apartments</p>
              <p className="mb-[4px] font-mono text-[22px] font-medium leading-none text-[#5b9cf6]">{apartmentsCount}</p>
              <p className="text-[10px] leading-[1.4] text-[#4a5070]">Units ohne Typ Co-Living</p>
            </div>
            <div className="relative overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#10121a] p-[13px_15px] transition-colors hover:border-[#242840]">
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-t-[10px] bg-[#22d3ee]" />
              <p className="mb-[4px] text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Co-Living Units</p>
              <p className="mb-[4px] font-mono text-[22px] font-medium leading-none text-[#22d3ee]">{coLivingCount}</p>
              <p className="text-[10px] leading-[1.4] text-[#4a5070]">Typ Co-Living</p>
            </div>
            <div className="relative overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#10121a] p-[13px_15px] transition-colors hover:border-[#242840]">
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-t-[10px] bg-[#edf0f7]" />
              <p className="mb-[4px] text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Rooms gesamt</p>
              <p className="mb-[4px] font-mono text-[22px] font-medium leading-none text-[#edf0f7]">{metricOrDash(summary.totalRooms)}</p>
              <p className="text-[10px] leading-[1.4] text-[#4a5070]">Erfasste Zimmerkapazität</p>
            </div>
            <div className="relative overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#10121a] p-[13px_15px] transition-colors hover:border-[#242840]">
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-t-[10px] bg-[#3ddc84]" />
              <p className="mb-[4px] text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Belegt</p>
              <p className="mb-[4px] font-mono text-[22px] font-medium leading-none text-[#3ddc84]">{metricOrDash(summary.occupiedRooms)}</p>
              <p className="text-[10px] leading-[1.4] text-[#4a5070]">Aktuell belegte Rooms</p>
            </div>
            <div className="relative overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#10121a] p-[13px_15px] transition-colors hover:border-[#242840]">
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-t-[10px] bg-[#f5a623]" />
              <p className="mb-[4px] text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Reserviert</p>
              <p className="mb-[4px] font-mono text-[22px] font-medium leading-none text-[#f5a623]">{metricOrDash(summary.reservedRooms)}</p>
              <p className="text-[10px] leading-[1.4] text-[#4a5070]">Noch nicht eingezogen</p>
            </div>
            <div className="relative overflow-hidden rounded-[10px] border border-[#1c2035] bg-[#10121a] p-[13px_15px] transition-colors hover:border-[#242840]">
              <div className="absolute left-0 right-0 top-0 h-[2px] rounded-t-[10px] bg-[#9d7cf4]" />
              <p className="mb-[4px] text-[9px] font-medium uppercase tracking-[0.5px] text-[#4a5070]">Belegungsquote</p>
              <p className="mb-[4px] font-mono text-[20px] font-medium leading-none text-[#9d7cf4]">
                {/*
                  THE BACKEND SENDS 0.0 AT ZERO ROOMS, and OC3 does not print
                  it. `occupancy_rate` is `(occupied / total) * 100 if total
                  else 0.0` server-side, so at a zero denominator the 0.0 is a
                  placeholder, not a measurement -- the API contract is
                  unchanged and this is the display rule. The `unavailable`
                  flag keeps OC2c's loading/error states out of the
                  zero-room branch: same glyph, different state.
                */}
                {occupancyPercentText({
                  total: summary.totalRooms,
                  rate: summary.occupancyRate,
                  unavailable: occupancyStatus !== "success",
                })}
              </p>
              <div className="mb-[4px] mt-[7px] h-[3px] rounded-full bg-[#191c28]">
                <div
                  className="h-full rounded-full bg-[#9d7cf4]"
                  style={{ width: `${quotePct}%` }}
                />
              </div>
              <p className="text-[10px] text-[#9d7cf4]">Nur belegte Rooms</p>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-[10px] flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.8px] text-[#4a5070]">
              Belegungsübersicht pro Unit
            </span>
            <div className="h-px flex-1 bg-[#1c2035]" />
          </div>
          <SectionCard
            title="Belegungsübersicht pro Unit"
            subtitle="Hier siehst du sofort, welche Units stark laufen und wo Leerstand besteht."
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Unit
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Ort
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Adresse
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Rooms gesamt
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Belegt
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Reserviert
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Frei
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Belegt %
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Reserviert %
                    </th>
                    <th className="whitespace-nowrap border-b border-[#1c2035] px-[14px] py-[8px] text-left text-[9px] font-medium uppercase tracking-[0.6px] text-[#4a5070]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {occupancyRows.map((row, idx, arr) => {
                    const bar = occupancyBarVisual(row.occupancyRate);
                    return (
                      <tr
                        key={row.unitId}
                        className={`border-b border-[#1c2035] text-[11px] text-[#8892b0] transition-colors hover:bg-[#141720] ${
                          idx === arr.length - 1 ? "border-b-0" : ""
                        }`}
                      >
                        <td className="align-middle px-[14px] py-[11px]">
                          <div className="flex flex-wrap items-center gap-2">
                            {row.unit ? <UnitTypeBadge unit={row.unit} /> : null}
                            <Link
                              to={`/admin/units/${encodeURIComponent(String(row.unitId))}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-mono text-[11px] font-medium text-[#5b9cf6] hover:underline"
                            >
                              {getUnitDisplayLabel(
                                row.unit || { unitId: row.unitId, id: row.unitId }
                              )}
                            </Link>
                          </div>
                          {row.unitId ? (
                            <span className="mt-[2px] block max-w-[180px] truncate font-mono text-[8px] text-[#4a5070]">
                              {row.unitId}
                            </span>
                          ) : null}
                        </td>
                        <td className="align-middle px-[14px] py-[11px] text-[11px] text-[#4a5070]">{row.place}</td>
                        <td className="align-middle px-[14px] py-[11px] text-[11px] text-[#5b9cf6]">
                          <UnitAddressActions
                            unit={row.unit}
                            fallbackUnitId={row.unitId}
                            displayText={
                              row.address || buildMapsQueryFromUnit(row.unit) || "—"
                            }
                          />
                        </td>
                        <td className="align-middle px-[14px] py-[11px] font-mono text-[11px] text-[#edf0f7]">
                          {row.totalRooms}
                        </td>
                        <td
                          className={`align-middle px-[14px] py-[11px] font-mono text-[11px] ${
                            row.occupiedCount === 0
                              ? "text-[#4a5070]"
                              : "font-medium text-[#3ddc84]"
                          }`}
                        >
                          {row.occupiedCount}
                        </td>
                        <td
                          className={`align-middle px-[14px] py-[11px] font-mono text-[11px] ${
                            row.reservedCount === 0 ? "text-[#4a5070]" : "text-[#f5a623]"
                          }`}
                        >
                          {row.reservedCount}
                        </td>
                        <td
                          className={`align-middle px-[14px] py-[11px] font-mono text-[11px] ${
                            row.freeCount === 0 ? "text-[#4a5070]" : "text-[#ff5f6d]"
                          }`}
                        >
                          {row.freeCount}
                        </td>
                        <td className="align-middle px-[14px] py-[11px]">
                          <div className="flex items-center gap-[6px]">
                            <div className="h-[4px] w-[70px] overflow-hidden rounded-full bg-[#191c28]">
                              <div
                                className={`h-full rounded-full ${bar.fill}`}
                                style={{ width: `${Math.min(100, Math.max(0, row.occupancyRate))}%` }}
                              />
                            </div>
                            <span className={`font-mono text-[10px] ${bar.text}`}>
                              {occupancyPercentText({
                                total: row.totalRooms,
                                rate: row.occupancyRate,
                              })}
                            </span>
                          </div>
                        </td>
                        <td className="align-middle px-[14px] py-[11px] font-mono text-[10px] text-[#4a5070]">
                          {occupancyPercentText({
                            total: row.totalRooms,
                            rate: row.reservedRate,
                          })}
                        </td>
                        <td className="align-middle px-[14px] py-[11px]">
                          <Badge type={getStatusBadgeType(row.displayStatus)}>
                            {row.displayStatus}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}

                  {occupancyRows.length === 0 && (
                    <tr>
                      <td
                        colSpan="10"
                        className="border-b-0 px-[14px] py-8 text-center text-[12px] text-[#4a5070]"
                        {...{ [emptyOccupancyState(occupancyStatus).attr]: true }}
                      >
                        {emptyOccupancyState(occupancyStatus).text}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div>
          <div className="mb-[10px] flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.8px] text-[#4a5070]">Raumstatus · Karte</span>
            <div className="h-px flex-1 bg-[#1c2035]" />
          </div>
          <SectionCard
            title="Raumstatus (Karte)"
            subtitle="Visuelle Karte: Belegt (grün), Reserviert (gelb), Frei (rot). Daten vom Backend (Tenancies)."
          >
            <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
              {coLivingUnits.map((unit) => (
                <OccupancyMap
                  key={unit.unitId ?? unit.id}
                  unit={unit}
                  rooms={rooms}
                  occupancyData={occupancyRoomsByUnit[unit.id ?? unit.unitId] ?? null}
                  loading={!occupancyRoomsSettled[unit.id ?? unit.unitId]}
                />
              ))}
              {coLivingUnits.length === 0 && (
                <p className="px-[18px] py-[16px] text-[12px] text-[#4a5070]">
                  Keine Co-Living Units. Räume werden pro Unit geladen.
                </p>
              )}
            </div>
          </SectionCard>
        </div>

        <SectionCard
          title="Schwächste Belegung"
          subtitle="Diese Units haben aktuell die tiefste Belegungsquote."
        >
          <div className="grid grid-cols-1 gap-3 px-[14px] py-[12px] md:grid-cols-2 xl:grid-cols-5">
            {weakestUnits.map((row) => (
              <div
                key={row.unitId}
                className="rounded-[10px] border border-[#1c2035] bg-[#141720] p-4"
              >
                <p className="text-[10px] text-[#4a5070]">{row.place}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {row.unit ? <UnitTypeBadge unit={row.unit} /> : null}
                  <div className="min-w-0 flex-1">
                    <UnitAddressActions
                      unit={row.unit}
                      fallbackUnitId={row.unitId}
                      displayText={getUnitDisplayLabel(
                        row.unit || { unitId: row.unitId, id: row.unitId }
                      )}
                      linkClassName="font-mono text-[13px] font-medium"
                    />
                  </div>
                </div>
                {row.unitId ? (
                  <p className="mt-0.5 max-w-[180px] truncate font-mono text-[8px] text-[#4a5070]">
                    {row.unitId}
                  </p>
                ) : null}
                <p className="mt-3 font-mono text-[22px] font-semibold text-[#ff5f6d]">
                  {occupancyPercentText({
                    total: row.totalRooms,
                    rate: row.occupancyRate,
                  })}
                </p>
                <p className="mt-2 text-[11px] text-[#4a5070]">
                  {row.occupiedCount} von {row.totalRooms} Rooms belegt
                </p>
              </div>
            ))}

            {weakestUnits.length === 0 && (
              <p
                className="col-span-full text-[12px] text-[#4a5070]"
                {...{ [emptyOccupancyState(occupancyStatus).attr]: true }}
              >
                {occupancyStatus === "success"
                  ? "Keine Daten vorhanden."
                  : emptyOccupancyState(occupancyStatus).text}
              </p>
            )}
          </div>
        </SectionCard>

        <div>
          <div className="mb-[10px] flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.8px] text-[#4a5070]">
              Freie Zimmer · Verfügbar ab
            </span>
            <div className="h-px flex-1 bg-[#1c2035]" />
          </div>
          <SectionCard
            title="Freie Zimmer / Verfügbar ab"
            subtitle="Operative Planung: Zimmer, die frei sind oder frei werden, inkl. bereits erfasster Nachmieter. Quelle: Backend planning_state pro Zimmer."
          >
            <div className="grid grid-cols-1 gap-3 px-[14px] py-[12px] md:grid-cols-2 xl:grid-cols-3">
              {availabilityRooms.map((row) => {
                const badges = roomPlanningBadges(row.room);
                return (
                  <Link
                    key={row.key}
                    to={`/admin/units/${encodeURIComponent(row.unitId)}`}
                    className="group block rounded-[10px] border border-[#1c2035] bg-[#141720] p-4 transition hover:border-[#2a3147] hover:bg-[#171a24]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        {row.unit ? <UnitTypeBadge unit={row.unit} /> : null}
                        <span className="truncate text-[12px] text-[#edf0f7]">
                          {row.unit ? getUnitDisplayLabel(row.unit) : row.unitId}
                        </span>
                      </div>
                      <span className="shrink-0 text-[10px] text-[#4a5070] group-hover:text-[#8892b0]">
                        Öffnen ›
                      </span>
                    </div>
                    <p className="mt-2 text-[13px] font-medium text-[#edf0f7]">
                      {row.room.room_name ?? row.room.room_id}
                    </p>
                    <p className="mt-1 text-[11px] text-[#7f8daa]">{row.label}</p>
                    <div className="mt-2 flex flex-wrap gap-[4px]">
                      {badges.isFreeNow ? <Badge type="neutral">Jetzt frei</Badge> : null}
                      {badges.endingSoon ? <Badge type="alert">Endet bald</Badge> : null}
                      {badges.hasSuccessor ? (
                        <Badge type="accent">Nachmieter vorhanden</Badge>
                      ) : null}
                    </div>
                  </Link>
                );
              })}

              {availabilityRooms.length === 0 && (
                <p className="col-span-full text-[12px] text-[#4a5070]">
                  Keine Zimmerdaten geladen. Planung wird pro Co-Living Unit geladen.
                </p>
              )}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

export default AdminOccupancyPage;
